import { DriverSocketMsgType, type DriverSocketMsg } from '@libs/types/message'
import { DriverService } from './driver'
import { LocalConfigRepository } from './local_config_repository'
import type { Config, ConfigRepositoryResult, OnboardConfig } from './config_repository'

export class OnboardConfigRepository extends LocalConfigRepository {
  async list(): Promise<ConfigRepositoryResult> {
    const onboard = await DriverService.instance.askType<OnboardConfig>(DriverSocketMsgType.MTOnboardCfgList)
    const config = await DriverService.instance.askType<Config>(DriverSocketMsgType.MTCfgList)
    return { config, onboard }
  }

  async change(index: number): Promise<ConfigRepositoryResult> {
    const result = await super.change(index)
    return await this.withFreshOnboard(result.config)
  }

  async create(name: string): Promise<ConfigRepositoryResult> {
    const result = await super.create(name)
    return await this.withFreshOnboard(result.config)
  }

  async delete(index: number): Promise<ConfigRepositoryResult> {
    const result = await super.delete(index)
    return await this.withFreshOnboard(result.config)
  }

  async rename(index: number, name: string): Promise<ConfigRepositoryResult> {
    const result = await super.rename(index, name)
    return await this.withFreshOnboard(result.config)
  }

  async import(name: string, dataBase64: string): Promise<ConfigRepositoryResult> {
    const result = await super.import(name, dataBase64)
    return await this.withFreshOnboard(result.config)
  }

  async importReplace(name: string, dataBase64: string, replaceIndex: number): Promise<ConfigRepositoryResult> {
    const result = await super.importReplace(name, dataBase64, replaceIndex)
    return await this.withFreshOnboard(result.config)
  }

  async syncCurrentToOnboard(slot: number): Promise<ConfigRepositoryResult> {
    const onboard = await DriverService.instance.askTypeAndData<OnboardConfig>({
      Type: DriverSocketMsgType.MTOnboardCfgSync,
      Data: { Value: slot },
    } as DriverSocketMsg<{ Value: number }>)
    const config = await DriverService.instance.askType<Config>(DriverSocketMsgType.MTCfgList)
    return { config, onboard }
  }

  async renameOnboard(slot: number, name: string): Promise<ConfigRepositoryResult> {
    const onboard = await DriverService.instance.askTypeAndData<OnboardConfig>({
      Type: DriverSocketMsgType.MTOnboardCfgRename,
      Data: { Slot: slot, Name: name },
    } as DriverSocketMsg<{ Slot: number; Name: string }>)
    const config = await DriverService.instance.askType<Config>(DriverSocketMsgType.MTCfgList)
    return { config, onboard }
  }

  async replaceSlot(profileIndex: number, targetSlot: number): Promise<ConfigRepositoryResult> {
    const config = await DriverService.instance.askTypeAndData<Config>({
      Type: DriverSocketMsgType.MTOnboardCfgReplaceSlot,
      Data: { ProfileIndex: profileIndex, TargetSlot: targetSlot },
    } as DriverSocketMsg<{ ProfileIndex: number; TargetSlot: number }>)
    return await this.withFreshOnboard(config)
  }

  private async withFreshOnboard(config: Config): Promise<ConfigRepositoryResult> {
    const onboard = await DriverService.instance.askType<OnboardConfig>(DriverSocketMsgType.MTOnboardCfgList)
    const latestConfig = await DriverService.instance.askType<Config>(DriverSocketMsgType.MTCfgList)
    return { config: latestConfig || config, onboard }
  }
}
